/**
 * TemplateLibrary – Main component for Settings → Documentation → Template Library.
 *
 * Tabbed Template Library: Appointments, Measures, Treatment Planning, Forms,
 * Administrative.
 *
 * Each category tab renders a TemplateCategoryTab with templates filtered by category
 * and TablePaginationFooter (table pagination on every category tab).
 *
 * Uses Mantine Tabs matching the existing patterns in SettingsPage and theme.ts.
 */
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container,
  Stack,
  Tabs,
  Text,
  Title,
} from '@mantine/core';
import {
  IconCalendarEvent,
  IconChartBar,
  IconClipboardList,
  IconForms,
  IconUsers,
} from '@tabler/icons-react';
import { TemplateCategoryTab } from './components';
import {
  TEMPLATE_CATEGORIES,
  TEMPLATE_CATEGORY_LABELS,
  TEMPLATE_CATEGORY_DESCRIPTIONS,
} from './types';
import {
  isTemplateLibraryTab,
  readTemplateLibraryTabFromSearch,
  type TemplateLibraryTab,
} from './templateLibraryUiState';

/** Icons for each tab (matching clinical/documentation theme) */
const TAB_ICONS: Record<TemplateLibraryTab, React.ElementType> = {
  appointments: IconCalendarEvent,
  measures: IconChartBar,
  'treatment-planning': IconClipboardList,
  forms: IconForms,
  administrative: IconUsers,
};

export function TemplateLibrary(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = readTemplateLibraryTabFromSearch(searchParams.toString());
  const [activeTab, setActiveTab] = useState<string | null>(tabFromUrl ?? 'appointments');
  /** Increment to trigger refetch in all category tabs (e.g. after save that moves a template to another category). */
  const [refetchKey, setRefetchKey] = useState(0);
  const onTemplateSaved = useCallback(() => {
    setRefetchKey((k) => k + 1);
  }, []);

  useEffect(() => {
    const nextTab = readTemplateLibraryTabFromSearch(searchParams.toString()) ?? 'appointments';
    if (searchParams.get('tab') === 'client-operations') {
      setActiveTab('administrative');
      setSearchParams({ tab: 'administrative' }, { replace: true });
      return;
    }
    setActiveTab((current) => (current === nextTab ? current : nextTab));
  }, [searchParams, setSearchParams]);

  const handleTabChange = useCallback(
    (value: string | null) => {
      setActiveTab(value);
      if (!value || !isTemplateLibraryTab(value)) return;
      if (value === 'appointments') {
        setSearchParams({}, { replace: true });
        return;
      }
      setSearchParams({ tab: value }, { replace: true });
    },
    [setSearchParams],
  );

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        {/* Section header */}
        <div>
          <Title order={2} mb="xs">
            Documentation
          </Title>
          <Text size="sm" c="dimmed">
            Manage documentation templates and preferences
          </Text>
        </div>

        {/* Template Library sub-header */}
        <div>
          <Text fw={600} size="lg" mb={4}>
            Template Library
          </Text>
          <Text size="sm" c="dimmed">
            Create, edit, and manage documentation templates used across your practice
          </Text>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tabs.List>
            {TEMPLATE_CATEGORIES.map((cat) => {
              const Icon = TAB_ICONS[cat];
              return (
                <Tabs.Tab key={cat} value={cat} leftSection={<Icon size={16} />}>
                  {TEMPLATE_CATEGORY_LABELS[cat]}
                </Tabs.Tab>
              );
            })}
          </Tabs.List>

          {/* Category tab panels */}
          {TEMPLATE_CATEGORIES.map((cat) => (
            <Tabs.Panel key={cat} value={cat} pt="lg">
              {/* Tab description from designs */}
              <Text size="sm" c="dimmed" mb="md">
                {TEMPLATE_CATEGORY_DESCRIPTIONS[cat]}
              </Text>
              <TemplateCategoryTab
                category={cat}
                refetchKey={refetchKey}
                onTemplateSaved={onTemplateSaved}
              />
            </Tabs.Panel>
          ))}
        </Tabs>
      </Stack>
    </Container>
  );
}
